import {
  CSSProperties,
  createRef,
  Component,
  MutableRefObject,
  Fragment,
} from 'react';
import styled, { css } from 'styled-components';
import { dispatchMouseEventOnTarget, moveCursor } from '../dropdownHelpers';
import {
  isDropdownAiOption,
  isDropdownAiSection,
  isDropdownButtonOption,
  isDropdownCustomContentOption,
  isDropdownDivider,
  isDropdownExpandableSection,
  isDropdownHeader,
  isDropdownHeaderWithIconButton,
  isDropdownLink,
  isDropdownMessage,
  isDropdownOption,
  isDropdownSliderOption,
  isDropdownSubmenu,
} from './settingsInterfaces';
import { isTruthy } from '@ardoq/common-helpers';
import { DEFAULT_ARDOQ_I18NLOCALE, t } from '@ardoq/i18n';
import { noop } from 'lodash';
import { Icon, ICON_SELECTOR, IconName, IconSize } from '@ardoq/icons';
import { AddListener, Listener } from './modalDropdownTypes';
import { ModalExpandableSection } from './ModalExpandableSection';
import { ModalSubmenu, RenderSubmenuList } from './ModalSubmenu';
import { Slider } from '@ardoq/forms';
import {
  DropdownAiOption,
  DropdownAiSection,
  DropdownItem,
  DropdownOption,
  DropdownOptionType,
  HoverTarget,
} from './types';
import { enhanceOptionsWithDefaults } from './utils';
import {
  DropdownMenuOption,
  DropdownMenuSectionSplit,
  DropdownMenuSectionTitle,
  DropdownMenuSectionWrapper,
  DropdownSize,
} from '@ardoq/dropdown-menu-ui';
import { colors, s16, s24, s4 } from '@ardoq/design-tokens';
import { IconButton } from '@ardoq/button';
import { Text } from '@ardoq/typography';
import { MenuOptionOuterWrapper } from '@ardoq/dropdown-menu-ui';

const SliderWrapper = styled.div<{
  $hideValue?: boolean;
  $hasRightLabel?: boolean;
}>`
  flex: 1 1 100%;
  margin: ${({ $hideValue, $hasRightLabel }) => {
    if ($hasRightLabel) {
      return `0 ${s4}`;
    }
    return `0 ${$hideValue ? s16 : 0} 0 ${s24}`;
  }};
`;

const HeaderWithIconButtonContainer = styled.div<{
  $hasDarkBackground: boolean;
}>`
  display: flex;
  ${({ $hasDarkBackground }) =>
    $hasDarkBackground &&
    css`
      > div {
        color: ${colors.textOnInvertedStrong};
      }
      > button {
        margin-left: auto;
      }
      background-color: ${colors.bgInvertedSubtle};
      ${ICON_SELECTOR} {
        color: ${colors.iconOnInvertedStrong};
      }
    `}
`;

const expandAiSection = (section: DropdownAiSection): DropdownItem[] => [
  {
    type: DropdownOptionType.HEADER,
    leftIcon: { name: IconName.AI_STAR },
    label: t(
      section.i18nLocale ?? DEFAULT_ARDOQ_I18NLOCALE,
      'dropdownMenu:aiActions',
      'AI Actions'
    ),
  },
  ...section.options.map((option): DropdownAiOption => ({
    // ensures the hover background of AI options is consistent even if the option doesn't have onClick defined
    onClick: noop,
    ...option,
    type: DropdownOptionType.AI_OPTION,
  })),
];

const getSettingsKeyWithOptionalPath = (
  key: string,
  settingsPath?: string[]
) => (settingsPath?.length ? `${settingsPath.join('-')}-${key}` : key);

const isActiveOption = (option: Partial<DropdownItem>) => {
  if (option.type !== DropdownOptionType.OPTION) {
    return false;
  }
  return (option as DropdownOption).isActive ?? false;
};

const getStateFocusedOnOption = (
  options: ModalListProps['options'],
  isFocusedOption: (option: Partial<DropdownItem>) => boolean
) => {
  const index = options.findIndex(isFocusedOption);
  return {
    cursor: index,
    hoverTarget: HoverTarget.NONE,
  };
};

export type ModalListProps = {
  options: Partial<DropdownItem>[];
  addKeyboardListener: AddListener;
  removeKeyboardListener: AddListener;
  closeMenu?: VoidFunction;
  dropdownStyle?: CSSProperties;
  iconStyle?: CSSProperties;
  minDropdownSize?: DropdownSize;
  maxDropdownSize?: DropdownSize;
  autoFocusActiveOption?: boolean;
};

type ExpandedState = Record<string, boolean>;

type ModaListState = {
  cursor: number;
  expandedState: ExpandedState;
  hoverTarget: HoverTarget;
};

export class ModalList extends Component<ModalListProps, ModaListState> {
  state: ModaListState = {
    cursor: -1,
    hoverTarget: HoverTarget.NONE,
    expandedState: {},
  };

  navigationOptions: DropdownItem[] = [];
  toggleExpandableSection: Record<string, () => void> = {};

  listRef: MutableRefObject<HTMLDivElement | null> = createRef();

  constructor(props: ModalListProps) {
    super(props);
    const expandedState: ExpandedState = Object.fromEntries(
      enhanceOptionsWithDefaults(this.props.options)
        .map((option, index) => {
          if (isDropdownExpandableSection(option)) {
            return [String(index), option.forceExpand || false];
          }
          return null;
        })
        .filter(isTruthy)
    );
    this.state = {
      ...this.state,
      expandedState,
    };
    this.toggleExpandableSection = Object.fromEntries(
      Object.keys(expandedState).map(key => [
        key,
        this.getToggleExpandableSectionState(key),
      ])
    );
  }

  onMouseOver = (event: MouseEvent) => {
    const current = this.listRef.current;
    if (!(current && event.target instanceof HTMLElement)) {
      return;
    }
    const li = event.target.closest('li');
    const index = Array.from(current.children).indexOf(li as any);
    const hoverTarget = event.target.closest('[data-hover-target]');
    if (
      index !== this.state.cursor ||
      this.state.hoverTarget === HoverTarget.NONE ||
      this.state.hoverTarget === HoverTarget.BUTTON ||
      hoverTarget
    ) {
      this.setState({
        cursor: index,
        hoverTarget: hoverTarget ? HoverTarget.BUTTON : HoverTarget.OPTION,
      });
    }
  };

  keyboardListener: Listener = event => {
    switch (event.key) {
      case 'Tab': {
        const delta = event.shiftKey ? -1 : 1;
        this.moveCursor(delta);
        return false;
      }

      case 'ArrowDown':
      case 'Down':
        this.moveCursor(1);
        return false;

      case 'ArrowUp':
      case 'Up':
        this.moveCursor(-1);
        return false;

      case 'ArrowRight':
      case 'Right': {
        const option = this.navigationOptions[this.state.cursor];
        if (this.listRef.current && option && isDropdownSubmenu(option)) {
          this.dispatchClickOnFocusedOption();
          return false;
        }
        break;
      }

      case 'Esc':
      case 'Escape':
      case 'ArrowLeft':
      case 'Left':
        if (this.props.closeMenu) {
          event.preventDefault();
          event.stopPropagation();
          this.props.closeMenu();
          return false;
        }
        break;

      case 'Enter': {
        const option = this.navigationOptions[this.state.cursor];
        if (
          this.listRef.current &&
          option &&
          (isDropdownOption(option) ||
            isDropdownSubmenu(option) ||
            isDropdownExpandableSection(option) ||
            isDropdownHeaderWithIconButton(option) ||
            isDropdownButtonOption(option))
        ) {
          // Stop the event from bubbling up to host component
          // which receives focus when the dropdown is closed
          event.preventDefault();
          event.stopPropagation();
          this.dispatchClickOnFocusedOption();
          return false;
        }
        break;
      }
    }
    return true;
  };

  getToggleExpandableSectionState(index: string) {
    return () => {
      const expandedState: ExpandedState = {
        ...this.state.expandedState,
        [index]: !this.state.expandedState[index],
      };
      this.setState({ expandedState });
    };
  }

  updateNavigationableOptions(expandedState: ExpandedState) {
    this.navigationOptions = enhanceOptionsWithDefaults(
      this.props.options
    ).flatMap((option, index) => {
      if (isDropdownExpandableSection(option)) {
        return [option, ...(expandedState[index] ? option.options : [])];
      }
      if (isDropdownAiSection(option)) {
        return expandAiSection(option);
      }
      return option;
    });
  }

  moveCursor(delta: number) {
    this.setState(({ cursor }) => ({
      cursor: moveCursor(this.navigationOptions, cursor, delta),
      hoverTarget: HoverTarget.NONE,
    }));
  }

  dispatchClickOnFocusedOption() {
    if (this.listRef.current) {
      const target = this.listRef.current.children[this.state.cursor];
      const iconButton =
        target &&
        target.querySelector(
          '[role="option"], [role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]'
        );
      dispatchMouseEventOnTarget(iconButton || target);
    }
  }

  componentDidMount() {
    this.props.addKeyboardListener(this.keyboardListener);
    document.addEventListener('mouseover', this.onMouseOver);

    const { autoFocusActiveOption, options } = this.props;
    if (autoFocusActiveOption) {
      const focusedState = getStateFocusedOnOption(options, isActiveOption);
      this.setState(focusedState);
    }

    const focusedByDefaultOptionIndex = options.findIndex(
      option => (option as DropdownOption).isFocusedByDefault ?? false
    );

    if (focusedByDefaultOptionIndex > -1) {
      // wrapped into setTimeout to run this after first "onMouseOver" which may occur right after menu open and reset cursor to -1
      setTimeout(() => {
        this.setState({
          cursor: focusedByDefaultOptionIndex,
          hoverTarget: HoverTarget.NONE,
        });
      }, 100);
    }
  }

  componentWillUnmount() {
    this.props.removeKeyboardListener(this.keyboardListener);
    document.removeEventListener('mouseover', this.onMouseOver);
    this.navigationOptions = [];
    this.toggleExpandableSection = {};
  }

  componentDidUpdate() {
    const listContainer = this.listRef.current;
    const scrollContainer = listContainer && listContainer.parentElement;
    const focusedItem =
      listContainer && listContainer.children[this.state.cursor];
    if (scrollContainer && focusedItem) {
      const containerBox = scrollContainer.getBoundingClientRect();
      const focusedBox = focusedItem.getBoundingClientRect();
      if (focusedBox.bottom > containerBox.bottom) {
        scrollContainer.scrollTop += focusedBox.bottom - containerBox.bottom;
      } else if (focusedBox.top < containerBox.top) {
        scrollContainer.scrollTop += focusedBox.top - containerBox.top;
      }
    }
  }

  render() {
    const { minDropdownSize, maxDropdownSize } = this.props;
    const { cursor, expandedState } = this.state;
    this.updateNavigationableOptions(expandedState);

    return (
      <DropdownMenuSectionWrapper ref={this.listRef} role="menu">
        {this.navigationOptions.map((option, index) => {
          if (isDropdownOption(option) || isDropdownAiOption(option)) {
            const {
              iconName = IconName.NONE,
              isActive,
              truncateLabel,
              ...rest
            } = option;
            const isAi =
              isDropdownAiOption(option) ||
              (isDropdownOption(option) && option.isAi === true);
            return (
              <DropdownMenuOption
                key={`option-${index}`}
                isFocused={index === cursor}
                leftIconName={iconName}
                isChecked={isActive}
                truncateTitle={truncateLabel}
                {...rest}
                isAi={isAi}
              />
            );
          }
          if (isDropdownButtonOption(option)) {
            const { iconName = IconName.NONE } = option;
            return (
              <DropdownMenuOption
                menuOptionWrapperProps={{
                  role: 'menuitem',
                  'aria-label':
                    typeof option.label === 'string' ? option.label : undefined,
                }}
                key={`button-option-${index}`}
                isFocused={index === cursor}
                leftIconName={iconName}
                {...option}
              />
            );
          }
          if (isDropdownExpandableSection(option)) {
            const { forceExpand } = option;
            return (
              <ModalExpandableSection
                key={`expandable-section-${index}`}
                toggleExpandedState={this.toggleExpandableSection[index]}
                isExpanded={expandedState[index] || false}
                focused={index === cursor}
                forceExpand={forceExpand === true}
              />
            );
          }
          if (isDropdownSubmenu(option)) {
            return (
              <MenuOptionOuterWrapper key={`submenu-${index}`} $isClickable>
                <ModalSubmenu
                  addKeyboardListener={this.props.addKeyboardListener}
                  removeKeyboardListener={this.props.removeKeyboardListener}
                  renderSubmenuList={renderSubmenuList}
                  focused={index === cursor}
                  maxDropdownSize={maxDropdownSize}
                  minDropdownSize={minDropdownSize}
                  {...option}
                />
              </MenuOptionOuterWrapper>
            );
          }
          if (isDropdownSliderOption(option)) {
            const {
              name,
              defaultValue,
              settingsPath,
              min,
              max,
              value,
              label,
              step,
              onValueChange,
              onValueChangeEnd,
              isDisabled,
              substituteMax,
              substituteMaxLabel,
              hideValue,
              rightLabel,
            } = option;
            const key = getSettingsKeyWithOptionalPath(name, settingsPath);
            return (
              <DropdownMenuOption
                key={key}
                label={label}
                isDisabled={isDisabled}
                menuOptionWrapperProps={{ style: { whiteSpace: 'nowrap' } }}
              >
                <SliderWrapper
                  $hideValue={hideValue}
                  $hasRightLabel={Boolean(rightLabel)}
                >
                  <Slider
                    onChange={onValueChange}
                    onChangeEnd={onValueChangeEnd}
                    step={step}
                    minValue={min}
                    maxValue={max}
                    defaultValue={defaultValue}
                    value={value}
                    substituteMax={substituteMax}
                    substituteMaxLabel={substituteMaxLabel}
                    isValueLabelVisible={!hideValue && !rightLabel}
                  />
                </SliderWrapper>
                {rightLabel ? <Text variant="text1">{rightLabel}</Text> : null}
              </DropdownMenuOption>
            );
          }
          if (isDropdownHeader(option)) {
            return (
              <DropdownMenuSectionTitle key={`header-${index}`}>
                {option.leftIcon && (
                  <Icon
                    iconName={option.leftIcon.name}
                    color={option.leftIcon.color}
                    iconSize={IconSize.SMALL}
                  />
                )}
                {option.label}
              </DropdownMenuSectionTitle>
            );
          }
          if (isDropdownHeaderWithIconButton(option)) {
            return (
              <HeaderWithIconButtonContainer
                key={`header-with-icon-button-${index}`}
                $hasDarkBackground={Boolean(option.hasDarkBackground)}
              >
                <DropdownMenuSectionTitle>
                  {option.label}
                </DropdownMenuSectionTitle>
                <IconButton
                  label={option.label}
                  iconName={option.iconName}
                  onClick={option.onClick}
                />
              </HeaderWithIconButtonContainer>
            );
          }
          if (isDropdownMessage(option)) {
            const { label } = option;
            return (
              <DropdownMenuOption
                key={`message-option-${index}`}
                label={label}
              />
            );
          }
          if (isDropdownLink(option)) {
            const { label, link, clickId, intercomTarget } = option;
            return (
              <DropdownMenuOption
                key={`link-option-${index}`}
                label={label || 'Read more'}
                intercomTarget={intercomTarget}
                clickId={clickId}
                onClick={() => window.open(link, '_blank')}
                rightIconName={IconName.OPEN_IN_NEW}
              />
            );
          }
          if (isDropdownCustomContentOption(option)) {
            return (
              <Fragment key={`custom-content-${index}`}>
                {option.content}
              </Fragment>
            );
          }
          if (isDropdownDivider(option)) {
            return <DropdownMenuSectionSplit key={`divider-${index}`} />;
          }
        })}
      </DropdownMenuSectionWrapper>
    );
  }
}

const renderSubmenuList: RenderSubmenuList = props => <ModalList {...props} />;
